import type { MountableApp } from '@opengov/psp-contract';
import { getCachedModule, loadModule } from './moduleLoader';
import {
  endsSessionWhenUnmounted,
  forgetSessionEnds,
  isPastSessionEndMaxAge,
  rememberedSessionEnds,
  SESSION_END_MAX_AGE_DAYS,
  sessionEndModuleUrl,
  type RememberedSessionEnd,
} from './sessionEndApps';

interface RegisteredModule {
  appId: string;
  module: MountableApp;
  /**
   * Tears the whole mount down (its shell bridge, contributions, and health poll, then
   * `module.unmount()`), once, throwing what `unmount()` throws. Defaults to `module.unmount()`.
   */
  teardown?: () => void;
}

const registered = new Set<RegisteredModule>();

const DEFAULT_SESSION_END_TIMEOUT_MS = 1500;

/**
 * Time for an app that is not mounted to load its module and end its session. Longer than a
 * mounted module's budget because it includes fetching the module, which may not be cached; kept
 * short because sign-out waits for it before the Auth0 redirect. An app that runs out of time
 * stays remembered for the next sign-out.
 */
const DEFAULT_UNMOUNTED_SESSION_END_TIMEOUT_MS = 2000;

/**
 * The loader's HEAD probe after a failed import, which classifies the failure (a 404, say). Short
 * so it reports inside the sign-out budget instead of the failure logging as a timeout.
 */
const UNMOUNTED_LOAD_PROBE_TIMEOUT_MS = 500;

interface FanoutOptions {
  /** Per-module timeout for `onSessionEnd`. Defaults to 1500 ms. */
  timeoutMs?: number;
  /**
   * Per-module timeout for a remembered module that is not mounted: loading it, then `onSessionEnd`.
   * Defaults to 2000 ms.
   */
  unmountedTimeoutMs?: number;
  /**
   * Called with the offending appId when a module's hook or unmount throws/rejects/times out, and
   * when an unmounted app has no module URL or its module fails to load.
   */
  onError?: (appId: string, err: unknown) => void;
  /** Called when the remembered session-end modules can't be read or cleared. */
  onStorageError?: (err: unknown) => void;
}

/**
 * Registers a mounted module so the shell can iterate it during logout fan-out.
 * Returns an idempotent dispose function — call it from the mount cleanup path so the
 * registry tracks only currently-mounted modules.
 */
export function registerMountedModule(entry: RegisteredModule): () => void {
  registered.add(entry);
  return () => {
    registered.delete(entry);
  };
}

/** Snapshot of currently-registered modules. */
export function getMountedModules(): readonly RegisteredModule[] {
  return Array.from(registered);
}

/** Test-only: reset the registry to empty between specs. */
// test-only export
export function clearMountedModulesRegistry(): void {
  registered.clear();
}

/**
 * Logout fan-out. For every registered module:
 *   1. Await `onSessionEnd()` with a per-module timeout — so a slow module cannot
 *      block other modules' cleanup or the Auth0 redirect.
 *   2. Tear the mount down (`teardown`, else `unmount()`) — best-effort, errors are
 *      swallowed via `onError`.
 *
 * Alongside them, every remembered module that opted in to `sessionEndWhenUnmounted`
 * has its session ended: by the mounted hook above when that module is the one
 * mounted now, otherwise by loading it (not mounting it) and awaiting its
 * `onSessionEnd` under its own timeout. Mounted modules unmount without waiting for
 * those loads; the fan-out, and so the Auth0 redirect, waits at most the longest
 * single budget. Remembered modules whose session ended are forgotten; one that
 * failed stays remembered, so the next sign-out tries it again, until
 * `SESSION_END_MAX_AGE_DAYS` after it last mounted, when it is reported and forgotten.
 *
 * The modules it unmounts leave the registry, so a subsequent invocation (e.g. a
 * retry after a failed redirect) does not re-fire hooks against torn-down modules.
 * A module that finishes mounting while the fan-out runs stays registered.
 */
export async function runSessionEndFanout(options: FanoutOptions = {}): Promise<void> {
  const {
    timeoutMs = DEFAULT_SESSION_END_TIMEOUT_MS,
    unmountedTimeoutMs = DEFAULT_UNMOUNTED_SESSION_END_TIMEOUT_MS,
    onError,
    onStorageError,
  } = options;
  const modules = getMountedModules();
  const remembered = readRemembered(onStorageError);

  const mountedSessionEnds = new Map<MountableApp, Promise<boolean>>();
  const mountedOutcomes = modules.map(({ appId, module }) => {
    const ended = invokeSessionEndHook({ appId, module, timeoutMs, onError });
    mountedSessionEnds.set(module, ended);
    return ended;
  });

  const rememberedOutcomes = remembered.map((entry) => {
    try {
      return endRememberedSession({
        entry,
        mountedSessionEnds,
        timeoutMs: unmountedTimeoutMs,
        onError,
      });
    } catch (err) {
      onError?.(entry.appId, err);
      return Promise.resolve(false);
    }
  });

  await Promise.allSettled(mountedOutcomes);

  // Before waiting on modules that aren't mounted, so a slow load can't hold up mounted modules'
  // teardown. `unmount()` is synchronous by contract (`MountableApp.unmount: () => void`),
  // so this loop needs no per-module timeout the way `onSessionEnd` does. If the
  // contract ever allows an async `unmount`, revisit this — a hung unmount here
  // would block the Auth0 redirect.
  for (const entry of modules) {
    registered.delete(entry);
    try {
      if (entry.teardown) entry.teardown();
      else entry.module.unmount();
    } catch (err) {
      onError?.(entry.appId, err);
    }
  }

  const settled = await Promise.allSettled(rememberedOutcomes);
  const now = Date.now();
  const forgotten = remembered.filter((entry, i) => {
    const outcome = settled[i];
    if (outcome.status === 'fulfilled' && outcome.value) return true;
    if (!isPastSessionEndMaxAge(entry, now)) return false;
    const reason =
      entry.rememberedAt === undefined
        ? 'has no record of when it mounted'
        : `was not ended within ${SESSION_END_MAX_AGE_DAYS} days of mounting`;
    onError?.(
      entry.appId,
      new Error(`${entry.appId} ${entry.routePattern} ${reason}, so it is no longer tried`)
    );
    return true;
  });

  try {
    forgetSessionEnds(forgotten);
  } catch (err) {
    onStorageError?.(err);
  }
}

function readRemembered(
  onStorageError: FanoutOptions['onStorageError']
): readonly RememberedSessionEnd[] {
  try {
    return rememberedSessionEnds();
  } catch (err) {
    onStorageError?.(err);
    return [];
  }
}

interface RememberedSessionEndArgs {
  entry: RememberedSessionEnd;
  /** The mounted modules' outcomes, by module. */
  mountedSessionEnds: ReadonlyMap<MountableApp, Promise<boolean>>;
  timeoutMs: number;
  onError?: (appId: string, err: unknown) => void;
}

/** Whether a remembered module no longer needs remembering. */
function endRememberedSession({
  entry,
  mountedSessionEnds,
  timeoutMs,
  onError,
}: RememberedSessionEndArgs): Promise<boolean> {
  const url = sessionEndModuleUrl(entry);
  if (!url) {
    onError?.(
      entry.appId,
      new Error(
        `No module URL for ${entry.appId} ${entry.routePattern}, so its session was not ended`
      )
    );
    return Promise.resolve(false);
  }
  if (url !== entry.mountedUrl) {
    // The route now resolves to another module (an app that loads one per entity, after an
    // entity switch). Ending that one wouldn't end this session, so keep it for a sign-out
    // where the route resolves to the module that mounted.
    onError?.(
      entry.appId,
      new Error(
        `${entry.appId} ${entry.routePattern} resolves to a different module than the one that mounted, so its session was not ended`
      )
    );
    return Promise.resolve(false);
  }
  // Matched by module, not app id: one app can serve a different module per route, and only the
  // module that is mounted now has had its hook called.
  const loaded = getCachedModule(url);
  const mountedEnd = loaded ? mountedSessionEnds.get(loaded) : undefined;
  return mountedEnd ?? endUnmountedModuleSession({ appId: entry.appId, url, timeoutMs, onError });
}

interface UnmountedSessionEndArgs {
  appId: string;
  url: string;
  timeoutMs: number;
  onError?: (appId: string, err: unknown) => void;
}

/**
 * Whether the module no longer needs remembering: its session ended, or the module now served at
 * its route no longer opts in, which a release that dropped the opt-in does deliberately.
 */
async function endUnmountedModuleSession({
  appId,
  url,
  timeoutMs,
  onError,
}: UnmountedSessionEndArgs): Promise<boolean> {
  const endSession = async () => {
    const module = await loadModule(url, {
      timeoutMs,
      probeTimeoutMs: UNMOUNTED_LOAD_PROBE_TIMEOUT_MS,
      retry: { maxAttempts: 1 },
    });
    if (endsSessionWhenUnmounted(module)) {
      await module.onSessionEnd();
    } else {
      // Still forgotten: this release asks not to be called. Reported so its owner can see the
      // session it kept is no longer ended for it.
      onError?.(
        appId,
        new Error(
          `${appId} no longer opts in to sessionEndWhenUnmounted, so its session was not ended`
        )
      );
    }
  };

  try {
    await withTimeout(
      endSession(),
      timeoutMs,
      `Ending the session of unmounted ${appId} timed out after ${timeoutMs}ms`
    );
    return true;
  } catch (err) {
    onError?.(appId, err);
    return false;
  }
}

async function withTimeout(work: Promise<void>, timeoutMs: number, message: string): Promise<void> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  try {
    await Promise.race([work, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

interface InvokeArgs {
  appId: string;
  module: MountableApp;
  timeoutMs: number;
  onError?: (appId: string, err: unknown) => void;
}

/** Whether the module's session ended: it had no hook, or the hook settled in time. */
async function invokeSessionEndHook({
  appId,
  module,
  timeoutMs,
  onError,
}: InvokeArgs): Promise<boolean> {
  const { onSessionEnd } = module;
  if (!onSessionEnd) return true;

  // Async IIFE so synchronous throws become rejections we can race against.
  const hookPromise = (async () => onSessionEnd.call(module))();

  try {
    await withTimeout(hookPromise, timeoutMs, `onSessionEnd timed out after ${timeoutMs}ms`);
    return true;
  } catch (err) {
    onError?.(appId, err);
    return false;
  }
}
